import React, { FC } from "react";
import { TableCell, TableRow, Box, Chip, Tooltip } from "@mui/material";
import { BaseTransactionDetailsDto } from "../../../../../api-client";
import { formatDateUI } from "../../../../../helpers/dateFormat";
import { FileText, Trash2 } from "lucide-react";
import { Button as ShadButton } from "@/components/ui/button";

interface BaseTransactionRowProps {
  transaction: BaseTransactionDetailsDto;
  onCancel: (transaction: BaseTransactionDetailsDto) => void;
  onDownloadPdf: (docId: string) => void;
}

/**
 * Shared row component for CASH and EC transactions
 * Both types share identical structure and visual indicators
 * Follows DRY principle by consolidating duplicate rendering logic
 */
export const BaseTransactionRow: FC<BaseTransactionRowProps> = ({
  transaction,
  onCancel,
  onDownloadPdf,
}) => {
  const isCancelled = transaction.isCancelled;
  const isCancellationTransaction = !!transaction.originalTransactionNumber;
  const isDisabled = isCancelled || isCancellationTransaction;

  return (
    <TableRow
      key={transaction.transactionNumber}
      sx={{
        backgroundColor:
          isCancelled || isCancellationTransaction ? "rgba(174, 143, 143, 0.13)" : "inherit",
        opacity: isCancelled || isCancellationTransaction ? 0.7 : 1,
      }}
    >
      {/* Transaction Number with STORNO Badge */}
      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span
            style={{
              textDecoration: isCancelled ? "line-through" : "none",
            }}
          >
            {transaction.transactionNumber}
          </span>
          {isCancellationTransaction && (
            <Chip label="STORNO" size="small" color="warning" sx={{ height: 20, fontSize: 11 }} />
          )}
        </Box>
      </TableCell>

      {/* RX Number */}
      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
        {transaction.rx ? transaction.rx.rxNumber : ""}
      </TableCell>

      {/* RX Date */}
      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
        {transaction.rx?.rxDate ? formatDateUI(transaction.rx.rxDate) : "Keine Datum"}
      </TableCell>

      {/* Patient Name */}
      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
        {transaction.patient
          ? `${transaction.patient.lastName}, ${transaction.patient.firstName}`
          : ""}
      </TableCell>

      {/* Text/Motiv */}
      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>{transaction.text}</TableCell>

      {/* Type */}
      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>{transaction.type}</TableCell>

      {/* Created By User */}
      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
        {transaction.user ? `${transaction.user.lastName}, ${transaction.user.firstName}` : ""}
      </TableCell>

      {/* Account (Mandant) */}
      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
        {transaction.account ? transaction.account.name : ""}
      </TableCell>

      {/* Created At */}
      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
        {formatDateUI(transaction.createdAt)}
      </TableCell>

      {/* Amount */}
      <TableCell
        align="right"
        sx={{
          padding: "5px 0px 5px 5px",
          color: transaction.amount < 0 ? "red" : "inherit",
          fontSize: 15,
        }}
      >
        {transaction.amount < 0
          ? `${Math.abs(Number(transaction.amount)).toFixed(2).replace(".", ",")} €`
          : `${Number(transaction.amount).toFixed(2).replace(".", ",")} €`}
      </TableCell>

      {/* Original Transaction Number */}
      <TableCell align="center" sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
        {transaction.originalTransactionNumber && (
          <Chip
            label={transaction.originalTransactionNumber}
            size="small"
            variant="outlined"
            sx={{ fontSize: 12 }}
          />
        )}
      </TableCell>

      {/* Document PDF */}
      <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
        {transaction.docDto && (
          <ShadButton
            variant="ghost"
            size="icon"
            className="h-8 w-8 border-0 bg-transparent text-[#367b9a] shadow-none hover:bg-transparent hover:text-[#367b9a] focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:size-6"
            onClick={() => onDownloadPdf(transaction.docDto!.id)}
          >
            <FileText />
          </ShadButton>
        )}
      </TableCell>

      {/* Actions - Delete/Cancel Button */}
      <TableCell align="center" sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
        <Tooltip
          title={
            isCancelled
              ? "Stornierte Transaktion kann nicht gelöscht werden"
              : isCancellationTransaction
                ? "Stornierungstransaktion kann nicht gelöscht werden"
                : "Transaktion stornieren"
          }
          arrow
        >
          <span>
            <ShadButton
              size="icon"
              onClick={() => onCancel(transaction)}
              disabled={isDisabled}
              className="h-7 w-7 border-0 bg-transparent text-slate-500 shadow-none transition-all duration-200 hover:scale-110 hover:bg-transparent hover:text-slate-700 focus-visible:ring-0 focus-visible:ring-offset-0 active:scale-95 disabled:pointer-events-none disabled:opacity-30 [&_svg]:size-5"
            >
              <Trash2 className="h-4 w-4" />
            </ShadButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};
